import { create } from 'zustand'
import { campaignsApi } from '@/lib/api'
import { captureEvent } from '@/lib/posthog'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { planLimitToastMessage } from '@/lib/planLimitMessages'
import type { Campaign, CampaignListItem, CampaignMember, CampaignStats, CampaignStatus, CreateCampaignData } from '@/types/campaign'

interface CampaignState {
  campaigns: CampaignListItem[]
  currentCampaign: Campaign | null
  stats: CampaignStats
  isLoading: boolean
  // "Ran by" team filter (Owner/Admin/Manager/Analyst only — the dropdown is
  // hidden for own-scoped roles and the API rejects them server-side anyway).
  members: CampaignMember[]
  ranByUserId: number | null

  fetchCampaigns: (skip?: number, limit?: number) => Promise<void>
  fetchCampaignById: (id: number) => Promise<Campaign>
  fetchStats: () => Promise<void>
  fetchMembers: () => Promise<void>
  setRanByFilter: (userId: number | null) => void
  createCampaign: (data: CreateCampaignData) => Promise<Campaign>
  deleteCampaign: (id: number) => Promise<void>
  launchCampaign: (id: number, approverId?: number | null) => Promise<void>
  pauseCampaign: (id: number) => Promise<void>
  resumeCampaign: (id: number) => Promise<void>
  setCurrentCampaign: (campaign: Campaign | null) => void

  // Reset — wipes per-user data on account switch (see main.tsx auth subscribe).
  reset: () => void
}

const initialState = {
  campaigns: [] as CampaignListItem[],
  currentCampaign: null as Campaign | null,
  stats: { total: 0, scheduled: 0, running: 0, completed: 0, paused: 0, failed: 0 } as CampaignStats,
  isLoading: false,
  members: [] as CampaignMember[],
  ranByUserId: null as number | null,
}

export const useCampaignStore = create<CampaignState>()((set, get) => ({
  ...initialState,

  fetchCampaigns: async (skip = 0, limit = 50) => {
    set({ isLoading: true })
    try {
      const ranBy = get().ranByUserId
      const response = ranBy != null
        ? await campaignsApi.getAll(skip, limit, ranBy)
        : await campaignsApi.getAll(skip, limit)
      set({ campaigns: response.data, isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      toast.error('Failed to fetch campaigns')
    }
  },

  fetchMembers: async () => {
    try {
      const response = await campaignsApi.getMembers()
      set({ members: response.data })
    } catch (error) {
      // silently fail — the filter dropdown simply stays hidden
    }
  },

  // Synchronous on purpose: the page resets its own pagination and refetches
  // with its page size right after (fetchCampaigns/fetchStats read the filter
  // from state).
  setRanByFilter: (userId: number | null) => set({ ranByUserId: userId }),

  fetchCampaignById: async (id: number) => {
    set({ isLoading: true })
    try {
      const response = await campaignsApi.getById(id)
      set({ currentCampaign: response.data, isLoading: false })
      return response.data
    } catch (error) {
      set({ isLoading: false })
      toast.error('Failed to fetch campaign')
      throw error
    }
  },

  fetchStats: async () => {
    try {
      const ranBy = get().ranByUserId
      const response = ranBy != null
        ? await campaignsApi.getStats(ranBy)
        : await campaignsApi.getStats()
      set({ stats: response.data })
    } catch (error) {
      // silently fail
    }
  },

  createCampaign: async (data: CreateCampaignData) => {
    try {
      const response = await campaignsApi.create(data)
      const { campaigns } = get()
      set({ campaigns: [response.data, ...campaigns] })
      toast.success('Campaign created successfully')
      return response.data
    } catch (error: any) {
      // The API call sets skipErrorToast, so this is the ONLY toast for any
      // create failure — planLimitToastMessage falls back to the real detail
      // (or this generic message) for the non-plan-limit case, so nothing
      // that used to surface here goes silent.
      const isTrial = useAuthStore.getState().planStatus?.is_trial ?? false
      const fallback = error?.response?.status === 409 ? 'Campaign with same name exists!' : 'Failed to create campaign'
      const { message, showUpgrade } = planLimitToastMessage(error, { isTrial, fallback })
      toast.error(message, showUpgrade ? { action: { label: 'Upgrade', onClick: () => { window.location.href = '/billing' } } } : undefined)
      throw error
    }
  },

  deleteCampaign: async (id: number) => {
    try {
      await campaignsApi.delete(id)
      const { campaigns } = get()
      set({ campaigns: campaigns.filter(c => c.id !== id) })
      toast.success('Campaign deleted')
    } catch (error) {
      toast.error('Failed to delete campaign')
      throw error
    }
  },

  launchCampaign: async (id: number, approverId?: number | null) => {
    try {
      const response = await campaignsApi.launch(id, approverId)
      // For a Representative the server queues an approval request and
      // returns 'pending_approval' — never assume 'running', or the row (and
      // the toast) would claim a send that hasn't been authorised yet.
      const nextStatus: CampaignStatus = response.data?.status ?? 'running'
      const { campaigns } = get()
      set({
        campaigns: campaigns.map(c => c.id === id ? { ...c, status: nextStatus } : c),
        currentCampaign: get().currentCampaign?.id === id ? response.data : get().currentCampaign
      })
      if (nextStatus === 'pending_approval') {
        toast.success('Launch requested. An Owner, Admin, or Manager needs to approve it.')
        return
      }
      captureEvent('campaign_launched', { campaign_id: id })
      toast.success('Campaign launched')
    } catch (error: any) {
      // Launch can now fail on the PLAN as well: validate_campaign_for_launch
      // refuses a workflow using a step the tier does not include, and its
      // reason contains "plan", so planLimitToastMessage turns it into a
      // message with an Upgrade action. Every other launch failure still
      // surfaces its own detail, unchanged.
      const isTrial = useAuthStore.getState().planStatus?.is_trial ?? false
      const { message, showUpgrade } = planLimitToastMessage(error, { isTrial, fallback: 'Failed to launch campaign' })
      toast.error(message, showUpgrade ? { action: { label: 'Upgrade', onClick: () => { window.location.href = '/billing' } } } : undefined)
      throw error
    }
  },

  pauseCampaign: async (id: number) => {
    try {
      const response = await campaignsApi.pause(id)
      const { campaigns } = get()
      set({
        campaigns: campaigns.map(c => c.id === id ? { ...c, status: 'paused' as const } : c),
        currentCampaign: get().currentCampaign?.id === id ? response.data : get().currentCampaign
      })
      toast.success('Campaign paused')
    } catch (error) {
      toast.error('Failed to pause campaign')
      throw error
    }
  },

  resumeCampaign: async (id: number) => {
    try {
      const response = await campaignsApi.resume(id)
      const { campaigns } = get()
      set({
        campaigns: campaigns.map(c => c.id === id ? { ...c, status: 'running' as const } : c),
        currentCampaign: get().currentCampaign?.id === id ? response.data : get().currentCampaign
      })
      toast.success('Campaign resumed')
    } catch (error) {
      toast.error('Failed to resume campaign')
      throw error
    }
  },

  setCurrentCampaign: (campaign) => set({ currentCampaign: campaign }),

  reset: () => set(initialState),
}))
